/**
 * Table de Pythagore — calculs PURS : astuce pour un produit, questions du
 * défi, saisie au pavé numérique.
 */

export const TABLE_SIZE = 10;
export const CHALLENGE_LENGTH = 20;
export const CHALLENGE_SECONDS = 60;

export function clampFactor(n: number): number {
  return Math.min(TABLE_SIZE, Math.max(1, Math.round(n)));
}

/** Une astuce pour retrouver a × b sans l'avoir apprise par cœur. */
export function tipFor(a: number, b: number): string {
  const lo = Math.min(a, b);
  const hi = Math.max(a, b);
  const p = a * b;
  if (lo === 1) return `Multiplier par 1 ne change rien : ${hi} × 1 = ${hi}.`;
  if (lo === 10 || hi === 10) {
    const o = lo === 10 ? hi : lo;
    return `× 10 : on ajoute un rang, ${o} unités deviennent ${o} dizaines, soit ${o * 10}.`;
  }
  if (a === b) return `Un carré : ${a} × ${a} = ${p}. Les carrés sont sur la diagonale.`;
  if (hi === 9) return `× 9, c’est × 10 moins une fois : ${lo} × 10 − ${lo} = ${p}.`;
  if (lo === 9) return `× 9, c’est × 10 moins une fois : ${hi} × 10 − ${hi} = ${p}.`;
  if (lo === 2) return `× 2, c’est le double : ${hi} + ${hi} = ${p}.`;
  if (lo === 5 || hi === 5) {
    const o = lo === 5 ? hi : lo;
    return `× 5, c’est la moitié de × 10 : ${o} × 10 = ${o * 10}, la moitié fait ${p}.`;
  }
  if (lo === 4 || hi === 4) {
    const o = lo === 4 ? hi : lo;
    return `× 4, c’est doubler deux fois : ${o} → ${o * 2} → ${p}.`;
  }
  if (lo === 3 || hi === 3) {
    const o = lo === 3 ? hi : lo;
    return `× 3, c’est le double plus une fois : ${o * 2} + ${o} = ${p}.`;
  }
  // 6, 7, 8 entre eux : on passe par le double de la moitié du facteur pair
  const even = hi % 2 === 0 ? hi : lo;
  const other = even === hi ? lo : hi;
  const half = even / 2;
  return `${a} × ${b} = ${other} × ${half} × 2 = ${other * half} × 2 = ${p}.`;
}

export interface TableQuestion {
  a: number;
  b: number;
}

/**
 * Questions du défi : facteurs de 2 à 9 (les tables de 1 et 10 sont trop
 * faciles), jamais deux fois le même produit d'affilée.
 */
export function makeQuestions(rng: () => number, n = CHALLENGE_LENGTH): TableQuestion[] {
  const out: TableQuestion[] = [];
  const pick = () => 2 + Math.min(7, Math.floor(rng() * 8));
  let guard = 0;
  while (out.length < n && guard < n * 50) {
    guard++;
    const q = { a: pick(), b: pick() };
    const prev = out[out.length - 1];
    if (prev && prev.a * prev.b === q.a * q.b) continue;
    out.push(q);
  }
  return out;
}

/** Saisie au pavé : 3 chiffres au plus, pas de zéro en tête. */
export function typeDigit(input: string, d: string): string {
  if (!/^[0-9]$/.test(d)) return input;
  if (input.length >= 3) return input;
  if (input === '0') return d;
  return input + d;
}

export function isRightAnswer(q: TableQuestion, input: string): boolean {
  return input !== '' && Number(input) === q.a * q.b;
}

/** « 18/20 en 47 s » */
export function challengeSummary(score: number, total: number, seconds: number): string {
  return `${score}/${total} en ${Math.max(0, Math.round(seconds))} s`;
}
